/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 23:49:06
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { SubmissionError } from 'redux-form';

/**
 * Build initial values for redux form
 *
 * @param  {object} customRangeSlider
 *
 * @return {object}           redux form for custom range slider
 */
export const buildInitialValues = initialValue => {
  const { id, attribute, valueType, prefix, excludedValues, manualValues } = initialValue;

  let tagManualValues = [];
  let tagExcludedValues = [];
  if (valueType === 'specific') {
    tagManualValues = manualValues
      ? manualValues.map(item => ({
          value: item,
          label: item
        }))
      : [];
  } else {
    tagExcludedValues = excludedValues
      ? excludedValues.map(item => ({
          value: item,
          label: item
        }))
      : [];
  }

  const result = {
    id,
    attribute,
    tagOptionValues: valueType,
    tagPrefix: prefix ? true : false,
    tagPrefixValue: prefix,
    tagManualValues,
    tagExcludedValues
  };
  return result;
};

/**
 * Validate custom range slider
 *
 * @param  {object} customRangeSlider
 * @param {Object} i18b
 * @return {boolean}           true | false
 */
export const validateBeforeSave = (customRangeSlider, i18n) => {
  const { tagPrefix, tagPrefixValue } = customRangeSlider;
  if (tagPrefix) {
    if (_.isEmpty(tagPrefixValue)) {
      throw new SubmissionError({
        tagPrefixValue: i18n.translate(
          'AdminHTML.CustomRangeSlider.Validate.invalid_TagPrefixValue_Empty'
        )
      });
    }

    if (/<[^ >]+>/g.test(tagPrefixValue)) {
      throw new SubmissionError({
        tagPrefixValue: i18n.translate(
          'AdminHTML.CustomRangeSlider.Validate.invalid_TagPrefixValue_SpecialCharacter'
        )
      });
    }
  }

  return true;
};

/**
 * Build custom range slider value for saving
 *
 * @param  {object} customRangeSlider
 * @return {object}           updated customRangeSlider
 */
export const buildCustomRangeSlider = customRangeSlider => {
  const {
    id,
    attribute,
    tagOptionValues,
    tagPrefix,
    tagPrefixValue,
    tagManualValues,
    tagExcludedValues
  } = customRangeSlider;

  let manualValues = null;
  let excludedValues = null;
  if (tagOptionValues === 'specific') {
    manualValues = tagManualValues ? tagManualValues.map(item => item.value) : null;
  } else {
    excludedValues = tagExcludedValues ? tagExcludedValues.map(item => item.value) : null;
  }

  const result = {
    id,
    attribute,
    valueType: tagOptionValues,
    prefix: tagPrefix && tagPrefixValue ? tagPrefixValue : undefined,
    manualValues: !_.isEmpty(manualValues) ? manualValues : undefined,
    excludedValues: !_.isEmpty(excludedValues) ? excludedValues : undefined
  };

  return result;
};

/**
 * Build list of option attribute for select
 *
 * @param  {Object}  options    list option type from tenant
 * @param  {Object}  metafields    list metafield from tenant
 * @param  {Object}  tags    tags from tenant
 *
 * @return {Object}  optionType list option type for select
 */
export const buildOptionAttributes = (options, metafields, tags, i18n) => {
  let optionAttributes = [];

  if (options && options.length > 0) {
    options.map(item => {
      if (item) {
        optionAttributes.push({
          value: `opt_${item.name}`,
          label: i18n.translate(
            'AdminHTML.CustomRangeSlider.CustomRangeSlider.productAttributes_Option',
            {
              nt_option_label: item.originalName
            }
          )
        });
      }
      return '';
    });
  }

  // Tags
  if (tags && tags.length > 0) {
    optionAttributes.push({
      value: 'tag',
      label: i18n.translate('AdminHTML.CustomRangeSlider.CustomRangeSlider.productAttributes_Tag')
    });
  }

  // Add metafields type
  if (metafields && metafields.length > 0) {
    metafields.map(item => {
      const split = item.split('::');
      optionAttributes.push({
        value: `metafield_::${split[1]}::${split[2]}`,
        label: i18n.translate(
          'AdminHTML.CustomRangeSlider.CustomRangeSlider.productAttributes_Metafield',
          {
            nt_key: split[1],
            nt_value: split[2]
          }
        )
      });
      return '';
    });
  }

  return optionAttributes;
};
